import { ApolloLink } from "@apollo/client";
import { ServerError } from "@apollo/client/errors";
import type { Operation } from "@apollo/client";
import { Observable } from "@apollo/client/utilities";
import { getMainDefinition } from "@apollo/client/utilities";

import {
  type ApiReachabilityHeaders,
  isGatewayUnavailableStatus,
  isNetworkUnreachableError,
  reportApiReachable,
  confirmApiUnreachable,
} from "./api-connectivity";

/**
 * The Apollo half of the reachability signal, kept out of `index.tsx` so it
 * can be exercised with a real link chain in a test (`index.tsx` reads a bare
 * `webConfig` global and cannot be imported under vitest).
 */

function isSubscriptionOperation(operation: Operation): boolean {
  const definition = getMainDefinition(operation.query);
  return (
    definition.kind === "OperationDefinition" &&
    definition.operation === "subscription"
  );
}

/**
 * Any successful HTTP result means the API is reachable again. Subscriptions
 * ride the WebSocket, which proves nothing about HTTP, so they are skipped.
 * Wraps the downstream observable by hand: Apollo 4's observables are RxJS
 * observables and carry no `.map` instance method.
 */
export const reachabilityLink = new ApolloLink((operation, forward) => {
  const subscription = isSubscriptionOperation(operation);
  return new Observable((observer) => {
    const inner = forward(operation).subscribe({
      next: (result) => {
        if (!subscription) reportApiReachable();
        observer.next(result);
      },
      error: observer.error.bind(observer),
      complete: observer.complete.bind(observer),
    });
    return () => inner.unsubscribe();
  });
});

/**
 * Classify an error-link failure. Returns the reason when the API could not be
 * reached (and starts confirmation), or null when it is an ordinary error.
 */
export function noteUnreachableIfSo(
  error: unknown,
  operation: Operation,
  graphqlUrl: string,
  getHeaders?: ApiReachabilityHeaders,
): "network" | "gateway" | null {
  if (isSubscriptionOperation(operation)) return null;
  if (ServerError.is(error)) {
    if (isGatewayUnavailableStatus(error.statusCode)) {
      confirmApiUnreachable(operation.operationName, graphqlUrl, getHeaders);
      return "gateway";
    }
    return null;
  }
  if (isNetworkUnreachableError(error)) {
    confirmApiUnreachable(operation.operationName, graphqlUrl, getHeaders);
    return "network";
  }
  return null;
}
